import React, { FunctionComponent, useMemo } from 'react'

import { useTheme } from 'styled-components/native'

import { CENTER_OF_EXCELLENCE, TOOLKIT_TYPE, WELLNESS_TOOLKIT_PROMOTION } from '@lyrahealth-inc/shared-app-logic'

import { ThemeType } from '../../utils'
import { withSuspense } from '../../utils/withSuspense'
import { BaseSkeleton } from '../baseSkeleton/BaseSkeleton'

export type WellnessToolkitCoverIllustrationProps = {
  promotion: TOOLKIT_TYPE
  isRoundImage?: boolean
  isSquareImage?: boolean
  illustrationProps?: { resizeToFillParentContainer?: boolean }
  /** Uses the previous desktop version (wider width card) */
  useOldVersion?: boolean
  enableSkeletonFallback?: boolean
  disableSquareImageOnMobile?: boolean
  isThumbnail?: boolean
}

export const WellnessToolkitCoverIllustration: FunctionComponent<WellnessToolkitCoverIllustrationProps> = ({
  promotion,
  isRoundImage = false,
  isSquareImage = false,
  useOldVersion = false,
  illustrationProps = {},
  enableSkeletonFallback = false,
  disableSquareImageOnMobile = false,
  isThumbnail = false,
}) => {
  const { breakpoints, colors } = useTheme() as ThemeType
  const shouldUseSquareImage = useMemo(
    () => (breakpoints.isMobileSized || isSquareImage) && !disableSquareImageOnMobile,
    [breakpoints.isMobileSized, disableSquareImageOnMobile, isSquareImage],
  )

  const illustrationLoadingSkeleton = useMemo(
    () =>
      enableSkeletonFallback ? (
        <BaseSkeleton
          containerStyle={{
            alignItems: 'flex-start',
          }}
          layout={[
            {
              width: 500,
              height: 500,
              borderRadius: 8,
              shadowColor: colors.shadowLow,
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.8,
              shadowRadius: 10,
              elevation: 5,
            },
          ]}
        />
      ) : undefined,
    [colors.shadowLow, enableSkeletonFallback],
  )

  const ToolkitIllustration = useMemo(() => {
    switch (promotion) {
      case WELLNESS_TOOLKIT_PROMOTION.WELLBEING:
        if (isRoundImage) {
          if (breakpoints.isMobileSized) {
            return withSuspense({
              component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitWellbeingRoundMobileSvg')),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            return withSuspense({
              component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitWellbeingRoundDesktopSvg')),
              fallback: illustrationLoadingSkeleton,
            })
          }
        } else {
          if (useOldVersion) {
            return withSuspense({
              component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitWellbeingOldDesktopSvg')),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            if (shouldUseSquareImage) {
              return withSuspense({
                component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitWellbeingMobile')),
                fallback: illustrationLoadingSkeleton,
              })
            } else {
              return withSuspense({
                component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitWellbeingDesktopSvg')),
                fallback: illustrationLoadingSkeleton,
              })
            }
          }
        }
        break
      case WELLNESS_TOOLKIT_PROMOTION.STRESS:
        if (isRoundImage) {
          if (breakpoints.isMobileSized) {
            return withSuspense({
              component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitStressRoundMobileSvg')),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            return withSuspense({
              component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitStressRoundDesktopSvg')),
              fallback: illustrationLoadingSkeleton,
            })
          }
        } else {
          if (useOldVersion) {
            return withSuspense({
              component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitStressOldDesktopSvg')),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            if (shouldUseSquareImage) {
              return withSuspense({
                component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitStressMobileSvg')),
                fallback: illustrationLoadingSkeleton,
              })
            } else {
              return withSuspense({
                component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitStressDesktopSvg')),
                fallback: illustrationLoadingSkeleton,
              })
            }
          }
        }
        break
      case WELLNESS_TOOLKIT_PROMOTION.RELATIONSHIPS:
        if (isRoundImage) {
          if (breakpoints.isMobileSized) {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitRelationshipsRoundMobileSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitRelationshipsRoundDesktopSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          }
        } else {
          if (useOldVersion) {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitRelationshipsOldDesktopSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            if (isThumbnail) {
              return withSuspense({
                component: React.lazy(
                  () => import('./wellnessToolkitCoverSvgs/WellnessToolkitRelationshipsThumbnailSvg'),
                ),
                fallback: illustrationLoadingSkeleton,
              })
            } else if (shouldUseSquareImage) {
              return withSuspense({
                component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitRelationshipsMobileSvg')),
                fallback: illustrationLoadingSkeleton,
              })
            } else {
              return withSuspense({
                component: React.lazy(
                  () => import('./wellnessToolkitCoverSvgs/WellnessToolkitRelationshipsDesktopSvg'),
                ),
                fallback: illustrationLoadingSkeleton,
              })
            }
          }
        }
        break
      case WELLNESS_TOOLKIT_PROMOTION.WORK:
        if (isRoundImage) {
          if (breakpoints.isMobileSized) {
            return withSuspense({
              component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitWorkRoundMobileSvg')),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            return withSuspense({
              component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitWorkRoundDesktopSvg')),
              fallback: illustrationLoadingSkeleton,
            })
          }
        } else {
          if (useOldVersion) {
            return withSuspense({
              component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitWorkOldDesktopSvg')),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            if (isThumbnail) {
              return withSuspense({
                component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitWorkThumbnailSvg')),
                fallback: illustrationLoadingSkeleton,
              })
            } else if (shouldUseSquareImage) {
              return withSuspense({
                component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitWorkMobile')),
                fallback: illustrationLoadingSkeleton,
              })
            } else {
              return withSuspense({
                component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitWorkDesktop')),
                fallback: illustrationLoadingSkeleton,
              })
            }
          }
        }
        break
      case WELLNESS_TOOLKIT_PROMOTION.PURPOSE:
        if (isRoundImage) {
          if (breakpoints.isMobileSized) {
            return withSuspense({
              component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitValuesRoundMobileSvg')),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            return withSuspense({
              component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitValuesRoundDesktopSvg')),
              fallback: illustrationLoadingSkeleton,
            })
          }
        } else {
          if (useOldVersion) {
            return withSuspense({
              component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitValuesOldDesktopSvg')),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            if (isThumbnail) {
              return withSuspense({
                component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitValuesThumbnailSvg')),
                fallback: illustrationLoadingSkeleton,
              })
            } else if (shouldUseSquareImage) {
              return withSuspense({
                component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitValuesMobileSvg')),
                fallback: illustrationLoadingSkeleton,
              })
            } else {
              return withSuspense({
                component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitValuesDesktopSvg')),
                fallback: illustrationLoadingSkeleton,
              })
            }
          }
        }
        break
      case WELLNESS_TOOLKIT_PROMOTION.PARENT:
        if (isRoundImage) {
          if (breakpoints.isMobileSized) {
            return withSuspense({
              component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitParentRoundMobileSvg')),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            return withSuspense({
              component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitParentRoundDesktopSvg')),
              fallback: illustrationLoadingSkeleton,
            })
          }
        } else {
          if (useOldVersion) {
            return withSuspense({
              component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitParentOldDesktopSvg')),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            if (isThumbnail) {
              return withSuspense({
                component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitParentThumbnailSvg')),
                fallback: illustrationLoadingSkeleton,
              })
            } else if (shouldUseSquareImage) {
              return withSuspense({
                component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitParentMobile')),
                fallback: illustrationLoadingSkeleton,
              })
            } else {
              return withSuspense({
                component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitParentDesktop')),
                fallback: illustrationLoadingSkeleton,
              })
            }
          }
        }
        break
      case WELLNESS_TOOLKIT_PROMOTION.WORKING_WITH_OTHERS:
        if (isRoundImage) {
          if (breakpoints.isMobileSized) {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitWorkingWithOthersRoundMobileSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitWorkingWithOthersRoundDesktopSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          }
        } else {
          if (useOldVersion) {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitWorkingWithOthersOldDesktopSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            if (shouldUseSquareImage) {
              return withSuspense({
                component: React.lazy(
                  () => import('./wellnessToolkitCoverSvgs/WellnessToolkitWorkingWithOthersMobileSvg'),
                ),
                fallback: illustrationLoadingSkeleton,
              })
            } else {
              return withSuspense({
                component: React.lazy(
                  () => import('./wellnessToolkitCoverSvgs/WellnessToolkitWorkingWithOthersDesktopSvg'),
                ),
                fallback: illustrationLoadingSkeleton,
              })
            }
          }
        }
        break
      case WELLNESS_TOOLKIT_PROMOTION.MINDFULNESS:
        if (isRoundImage) {
          if (breakpoints.isMobileSized) {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitMindfulnessRoundMobileSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitMindfulnessRoundDesktopSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          }
        } else {
          if (useOldVersion) {
            return withSuspense({
              component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitMindfulnessOldDesktopSvg')),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            if (shouldUseSquareImage) {
              return withSuspense({
                component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitMindfulnessMobileSvg')),
                fallback: illustrationLoadingSkeleton,
              })
            } else {
              return withSuspense({
                component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitMindfulnessDesktopSvg')),
                fallback: illustrationLoadingSkeleton,
              })
            }
          }
        }
        break
      case WELLNESS_TOOLKIT_PROMOTION.SELF_COMPASSION:
        if (isRoundImage) {
          if (breakpoints.isMobileSized) {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitSelfCompassionRoundMobileSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitSelfCompassionRoundDesktopSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          }
        } else {
          if (useOldVersion) {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitSelfCompassionOldDesktopSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            if (shouldUseSquareImage) {
              return withSuspense({
                component: React.lazy(
                  () => import('./wellnessToolkitCoverSvgs/WellnessToolkitSelfCompassionMobileSvg'),
                ),
                fallback: illustrationLoadingSkeleton,
              })
            } else {
              return withSuspense({
                component: React.lazy(
                  () => import('./wellnessToolkitCoverSvgs/WellnessToolkitSelfCompassionDesktopSvg'),
                ),
                fallback: illustrationLoadingSkeleton,
              })
            }
          }
        }
        break
      case WELLNESS_TOOLKIT_PROMOTION.LIFE_TRANSITIONS:
        if (isRoundImage) {
          if (breakpoints.isMobileSized) {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitLifeTransitionsRoundMobileSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitLifeTransitionsRoundDesktopSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          }
        } else {
          if (useOldVersion) {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitLifeTransitionsOldDesktopSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            if (shouldUseSquareImage) {
              return withSuspense({
                component: React.lazy(
                  () => import('./wellnessToolkitCoverSvgs/WellnessToolkitLifeTransitionsMobileSvg'),
                ),
                fallback: illustrationLoadingSkeleton,
              })
            } else {
              return withSuspense({
                component: React.lazy(
                  () => import('./wellnessToolkitCoverSvgs/WellnessToolkitLifeTransitionsDesktopSvg'),
                ),
                fallback: illustrationLoadingSkeleton,
              })
            }
          }
        }
        break
      case WELLNESS_TOOLKIT_PROMOTION.CAREGIVER_STRESS:
        if (isRoundImage) {
          if (breakpoints.isMobileSized) {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitCaregiverStressRoundMobileSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitCaregiverStressRoundDesktopSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          }
        } else {
          if (useOldVersion) {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitCaregiverStressOldDesktopSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            if (shouldUseSquareImage) {
              return withSuspense({
                component: React.lazy(
                  () => import('./wellnessToolkitCoverSvgs/WellnessToolkitCaregiverStressMobileSvg'),
                ),
                fallback: illustrationLoadingSkeleton,
              })
            } else {
              return withSuspense({
                component: React.lazy(
                  () => import('./wellnessToolkitCoverSvgs/WellnessToolkitCaregiverStressDesktopSvg'),
                ),
                fallback: illustrationLoadingSkeleton,
              })
            }
          }
        }
        break
      case WELLNESS_TOOLKIT_PROMOTION.FAMILY:
        if (isThumbnail) {
          return withSuspense({
            component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitFamilyThumbnailSvg')),
            fallback: illustrationLoadingSkeleton,
          })
        } else if (shouldUseSquareImage) {
          return withSuspense({
            component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitFamilyMobileSvg')),
            fallback: illustrationLoadingSkeleton,
          })
        } else {
          return withSuspense({
            component: React.lazy(() => import('./wellnessToolkitCoverSvgs/WellnessToolkitFamilyDesktopSvg')),
            fallback: illustrationLoadingSkeleton,
          })
        }
        break
      case WELLNESS_TOOLKIT_PROMOTION.TRAUMA:
        if (isRoundImage) {
          if (breakpoints.isMobileSized) {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitCriticalIncidentSupportRoundMobile'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitCriticalIncidentSupportRoundDesktopSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          }
        } else {
          if (isThumbnail) {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitCriticalIncidentSupportThumbnailSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          } else if (shouldUseSquareImage) {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitCriticalIncidentSupportMobileSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          } else {
            return withSuspense({
              component: React.lazy(
                () => import('./wellnessToolkitCoverSvgs/WellnessToolkitCriticalIncidentSupportDesktopSvg'),
              ),
              fallback: illustrationLoadingSkeleton,
            })
          }
        }
        break
      case CENTER_OF_EXCELLENCE.NEURODIVERSITY:
        if (isThumbnail) {
          return withSuspense({
            component: React.lazy(
              () => import('./centerOfExcellenceToolkitCoverSvgs/CenterOfExcellenceToolkitNeurodiversityThumbnailSvg'),
            ),
            fallback: illustrationLoadingSkeleton,
          })
        } else if (shouldUseSquareImage) {
          return withSuspense({
            component: React.lazy(
              () => import('./centerOfExcellenceToolkitCoverSvgs/CenterOfExcellenceToolkitNeurodiversityMobileSvg'),
            ),
            fallback: illustrationLoadingSkeleton,
          })
        } else {
          return withSuspense({
            component: React.lazy(
              () => import('./centerOfExcellenceToolkitCoverSvgs/CenterOfExcellenceToolkitNeurodiversityDesktopSvg'),
            ),
            fallback: illustrationLoadingSkeleton,
          })
        }
        break
    }
    return null
  }, [
    promotion,
    isRoundImage,
    illustrationLoadingSkeleton,
    breakpoints.isMobileSized,
    useOldVersion,
    shouldUseSquareImage,
    isThumbnail,
  ])

  return ToolkitIllustration ? <ToolkitIllustration {...illustrationProps} /> : <></>
}
